import React from 'react';
import { highlightMatches } from '../../utils/textUtils';

/**
 * Componente para mostrar texto con palabras resaltadas según un término de búsqueda
 *
 * @param {Object} props - Propiedades del componente
 * @param {string} props.text - Texto a mostrar
 * @param {string} props.searchTerm - Término de búsqueda para resaltar coincidencias
 * @param {string} props.className - Clase CSS adicional para el contenedor (opcional)
 *
 * @example
 * <HighlightedText
 *   text="María Paz Moreno González"
 *   searchTerm="maria moreno"
 * />
 * // Renderiza: <María> Paz <Moreno> González
 * // (donde <> indica texto resaltado)
 *
 * @example
 * <HighlightedText
 *   text="Juan Carlos Pérez"
 *   searchTerm="perez juan"
 * />
 * // Renderiza: <Juan> Carlos <Pérez>
 *
 * @description
 * Este componente:
 * - Usa fuzzy matching para encontrar coincidencias (insensible a acentos, mayúsculas)
 * - Soporta búsqueda tokenizada (palabras en cualquier orden)
 * - Preserva el formato original del texto
 * - Aplica clase CSS 'highlight' a las coincidencias
 */
function HighlightedText({ text, searchTerm, className = '' }) {
  // Si no hay texto, retornar null
  if (!text) return null;

  // Si no hay término de búsqueda, retornar texto sin resaltar
  if (!searchTerm || searchTerm.trim() === '') {
    return <span className={className}>{text}</span>;
  }

  // Obtener segmentos con información de resaltado
  const segments = highlightMatches(text, searchTerm);

  return (
    <span className={className}>
      {segments.map((segment, index) => (
        segment.highlight ? (
          <mark key={index} className="highlight">
            {segment.text}
          </mark>
        ) : (
          <span key={index}>{segment.text}</span>
        )
      ))}
    </span>
  );
}

export default HighlightedText;
